import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { Testimonial } from '../Testimonial/Testimonial';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import type { CtaAction } from '../../utils/ctaAction';
import './TestimonialCarousel.scss';

/**
 * Auto-rotation cadence. Advances to the next testimonial each tick; pauses
 * on hover/focus (WCAG 2.2.2) and is disabled under prefers-reduced-motion.
 */
const AUTO_ROTATE_MS = 5000;

/** Reconstructed testimonial assembled from the flat props. */
export interface TestimonialCarouselItem {
  quote: string;
  name: string;
  title?: string;
  eyebrow?: string;
  eyebrowLogoSrc?: string;
  eyebrowLogoAlt?: string;
  heading?: string;
  headshotSrc?: string;
  headshotAlt?: string;
  mediaSrc?: string;
  mediaAlt?: string;
  videoUrl?: string;
  linkText?: string;
  linkHref?: string;
  linkAction?: CtaAction;
  linkTargetPopupId?: string;
  ctaText?: string;
  ctaHref?: string;
  ctaAction?: CtaAction;
  ctaTargetPopupId?: string;
}

/** Flat, per-testimonial props for slots 1-6. A slot renders when both its quote and name are set. */
export interface TestimonialCarouselSlotProps {
  /** Testimonial 1 */
  testimonial1Quote: string;
  testimonial1Name: string;
  testimonial1Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial1Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial1EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial1EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial1Heading?: string;
  /** @polyType Url */
  testimonial1HeadshotSrc?: string;
  testimonial1HeadshotAlt?: string;
  /** @polyType Url */
  testimonial1MediaSrc?: string;
  testimonial1MediaAlt?: string;
  /** @polyType Url */
  testimonial1VideoUrl?: string;
  testimonial1LinkText?: string;
  /** @polyType Url */
  testimonial1LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial1LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial1LinkTargetPopupId?: string;
  testimonial1CtaText?: string;
  /** @polyType Url */
  testimonial1CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial1CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial1CtaTargetPopupId?: string;

  /** Testimonial 2 */
  testimonial2Quote?: string;
  testimonial2Name?: string;
  testimonial2Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial2Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial2EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial2EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial2Heading?: string;
  /** @polyType Url */
  testimonial2HeadshotSrc?: string;
  testimonial2HeadshotAlt?: string;
  /** @polyType Url */
  testimonial2MediaSrc?: string;
  testimonial2MediaAlt?: string;
  /** @polyType Url */
  testimonial2VideoUrl?: string;
  testimonial2LinkText?: string;
  /** @polyType Url */
  testimonial2LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial2LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial2LinkTargetPopupId?: string;
  testimonial2CtaText?: string;
  /** @polyType Url */
  testimonial2CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial2CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial2CtaTargetPopupId?: string;

  /** Testimonial 3 */
  testimonial3Quote?: string;
  testimonial3Name?: string;
  testimonial3Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial3Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial3EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial3EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial3Heading?: string;
  /** @polyType Url */
  testimonial3HeadshotSrc?: string;
  testimonial3HeadshotAlt?: string;
  /** @polyType Url */
  testimonial3MediaSrc?: string;
  testimonial3MediaAlt?: string;
  /** @polyType Url */
  testimonial3VideoUrl?: string;
  testimonial3LinkText?: string;
  /** @polyType Url */
  testimonial3LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial3LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial3LinkTargetPopupId?: string;
  testimonial3CtaText?: string;
  /** @polyType Url */
  testimonial3CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial3CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial3CtaTargetPopupId?: string;

  /** Testimonial 4 */
  testimonial4Quote?: string;
  testimonial4Name?: string;
  testimonial4Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial4Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial4EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial4EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial4Heading?: string;
  /** @polyType Url */
  testimonial4HeadshotSrc?: string;
  testimonial4HeadshotAlt?: string;
  /** @polyType Url */
  testimonial4MediaSrc?: string;
  testimonial4MediaAlt?: string;
  /** @polyType Url */
  testimonial4VideoUrl?: string;
  testimonial4LinkText?: string;
  /** @polyType Url */
  testimonial4LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial4LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial4LinkTargetPopupId?: string;
  testimonial4CtaText?: string;
  /** @polyType Url */
  testimonial4CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial4CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial4CtaTargetPopupId?: string;

  /** Testimonial 5 */
  testimonial5Quote?: string;
  testimonial5Name?: string;
  testimonial5Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial5Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial5EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial5EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial5Heading?: string;
  /** @polyType Url */
  testimonial5HeadshotSrc?: string;
  testimonial5HeadshotAlt?: string;
  /** @polyType Url */
  testimonial5MediaSrc?: string;
  testimonial5MediaAlt?: string;
  /** @polyType Url */
  testimonial5VideoUrl?: string;
  testimonial5LinkText?: string;
  /** @polyType Url */
  testimonial5LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial5LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial5LinkTargetPopupId?: string;
  testimonial5CtaText?: string;
  /** @polyType Url */
  testimonial5CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial5CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial5CtaTargetPopupId?: string;

  /** Testimonial 6 */
  testimonial6Quote?: string;
  testimonial6Name?: string;
  testimonial6Title?: string;
  /** @polyHelp Short label above the quote. Use this OR the Eyebrow Logo, never both — the logo takes precedence. */
  testimonial6Eyebrow?: string;
  /**
   * @polyType Url
   * @polyHelp Brand logo above the quote. Use this OR the Eyebrow Text, never both — the logo takes precedence.
   */
  testimonial6EyebrowLogoSrc?: string;
  /** @polyHelp Required when Eyebrow Logo URL is set — name the brand shown in the logo. */
  testimonial6EyebrowLogoAlt?: string;
  /** @polyHelp Short header above the quote and below the eyebrow or brand logo. Leave empty when the quote stands alone. */
  testimonial6Heading?: string;
  /** @polyType Url */
  testimonial6HeadshotSrc?: string;
  testimonial6HeadshotAlt?: string;
  /** @polyType Url */
  testimonial6MediaSrc?: string;
  testimonial6MediaAlt?: string;
  /** @polyType Url */
  testimonial6VideoUrl?: string;
  testimonial6LinkText?: string;
  /** @polyType Url */
  testimonial6LinkHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial6LinkAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial6LinkTargetPopupId?: string;
  testimonial6CtaText?: string;
  /** @polyType Url */
  testimonial6CtaHref?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  testimonial6CtaAction?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  testimonial6CtaTargetPopupId?: string;
}

/**
 * @polySemanticUsage About this block: displays social proof — customer quotes, success stories,
 * or endorsements — as a carousel of testimonial panels. Each panel typically includes the quote
 * text, attribution (name, title, company or brand), and an optional headshot or logo. Use it to
 * build trust and credibility.
 * Fill each testimonial{N}Quote with the raw testimonial text and no surrounding quotation
 * marks of any kind (" “ ” « » „) — every panel renders the standalone Testimonial component,
 * which adds the marks itself in the style of the page's language.
 * Each panel's eyebrow slot takes either a brand logo (testimonial{N}EyebrowLogoSrc) or short
 * eyebrow text (testimonial{N}Eyebrow), never both — populate exactly one per panel. When both
 * are set the logo renders and the text is dropped.
 * If a source testimonial carries a short header built into the panel, place it in
 * testimonial{N}Heading — it renders below the panel's eyebrow or brand logo and above the
 * quote. Do not place headers in the panel eyebrow.
 */
export interface TestimonialCarouselProps extends TestimonialCarouselSlotProps {
  /** Section heading (renders as h2 beside the nav arrows) */
  heading?: string;
  /**
   * Background variant applied to every testimonial panel.
   * @polyOptions White=white,Off-White=off-white
   * @polyDefault off-white
   */
  variant?: 'white' | 'off-white';
  /**
   * Accessible name for the carousel. Falls back to the heading, then
   * "Testimonials". No `@polyDefault`: a stored default would override the
   * heading fallback on every new block.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for this carousel. Leave empty to use the heading.
   */
  ariaLabel?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

/** Assemble a testimonial from the flat props for a given slot (1-6). */
function getTestimonial(
  props: TestimonialCarouselSlotProps,
  slot: 1 | 2 | 3 | 4 | 5 | 6,
): TestimonialCarouselItem | null {
  const quote = props[`testimonial${slot}Quote`];
  const name = props[`testimonial${slot}Name`];
  if (!quote || !name) return null;
  return {
    quote,
    name,
    title: props[`testimonial${slot}Title`],
    eyebrow: props[`testimonial${slot}Eyebrow`],
    eyebrowLogoSrc: props[`testimonial${slot}EyebrowLogoSrc`],
    eyebrowLogoAlt: props[`testimonial${slot}EyebrowLogoAlt`],
    heading: props[`testimonial${slot}Heading`],
    headshotSrc: props[`testimonial${slot}HeadshotSrc`],
    headshotAlt: props[`testimonial${slot}HeadshotAlt`],
    mediaSrc: props[`testimonial${slot}MediaSrc`],
    mediaAlt: props[`testimonial${slot}MediaAlt`],
    videoUrl: props[`testimonial${slot}VideoUrl`],
    linkText: props[`testimonial${slot}LinkText`],
    linkHref: props[`testimonial${slot}LinkHref`],
    linkAction: props[`testimonial${slot}LinkAction`],
    linkTargetPopupId: props[`testimonial${slot}LinkTargetPopupId`],
    ctaText: props[`testimonial${slot}CtaText`],
    ctaHref: props[`testimonial${slot}CtaHref`],
    ctaAction: props[`testimonial${slot}CtaAction`],
    ctaTargetPopupId: props[`testimonial${slot}CtaTargetPopupId`],
  };
}

/**
 * TestimonialCarousel — full-width Testimonial panels with carousel rotation.
 *
 * A heading row (h2 left, prev/next chevrons right) above a single visible
 * Testimonial panel. Each slot renders the standalone Testimonial component,
 * so every testimonial content variation (media, video, eyebrow logo,
 * headshot, link, CTA) is supported per panel. Panels crossfade on change.
 *
 * Auto-rotation advances every 5s and wraps to the first panel; it pauses
 * while the pointer hovers, keyboard focus is inside the section, a panel
 * video is playing, or the reader has toggled the explicit pause control
 * (WCAG 2.2.2 — a persistent, touch-reachable stop). Rotation is disabled
 * entirely under prefers-reduced-motion. The chevrons step one panel and
 * disable at the ends; manual navigation restarts the rotation interval so
 * the next auto-advance is a full cycle away.
 *
 * The editor contract is 2-6 testimonials. The flat-prop interface caps the
 * maximum structurally; a single-panel configuration still renders (without
 * nav or rotation) but flags a dev warning so the misconfig is visible.
 */
export const TestimonialCarousel: React.FC<TestimonialCarouselProps> = ({
  heading,
  variant = 'off-white',
  ariaLabel,
  anchorId,
  ...slotProps
}) => {
  const items = ([1, 2, 3, 4, 5, 6] as const)
    .map(slot => getTestimonial(slotProps as TestimonialCarouselSlotProps, slot))
    .filter((item): item is TestimonialCarouselItem => item !== null);

  const [activeIndex, setActiveIndex] = useState(0);
  const [restartTrigger, setRestartTrigger] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const prevButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const pauseButtonRef = useRef<HTMLButtonElement | null>(null);
  // Set only by manual chevron navigation, so the focus-handoff effect below
  // never steals focus on mount or during auto-rotation.
  const focusHandoffRef = useRef(false);

  // Rotation pauses while ANY engagement signal is active: the explicit
  // pause control (userPaused, WCAG 2.2.2), pointer hover, keyboard focus
  // inside the section, or an open testimonial video. Hover/focus/video are
  // transient and tracked as refs so they never force a re-render; the
  // explicit control is React state because it drives the button's label
  // and icon. mouse-leave cannot resume rotation while a keyboard user still
  // has focus inside, and rotation cannot advance a panel out from under an
  // open video.
  const isHoveredRef = useRef(false);
  const isFocusedRef = useRef(false);
  const isVideoOpenRef = useRef(false);
  const isPausedRef = useRef(false);
  const syncPause = useCallback(() => {
    isPausedRef.current =
      userPaused || isHoveredRef.current || isFocusedRef.current || isVideoOpenRef.current;
  }, [userPaused]);

  // Track prefers-reduced-motion reactively. Initialized false so the server
  // render and first client render agree (SSR has no matchMedia); the real
  // value is read after mount and on change. Rotation and the pause control
  // both key off this, so a reduced-motion user never sees a control that
  // toggles nothing.
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    if (prefersReducedMotion) return;

    const timer = setInterval(() => {
      if (isPausedRef.current) return;
      setActiveIndex(prev => (prev + 1) % items.length);
    }, AUTO_ROTATE_MS);

    return () => clearInterval(timer);
  }, [items.length, restartTrigger, prefersReducedMotion]);

  const handleMouseEnter = useCallback(() => { isHoveredRef.current = true; syncPause(); }, [syncPause]);
  const handleMouseLeave = useCallback(() => { isHoveredRef.current = false; syncPause(); }, [syncPause]);
  const handleFocusIn = useCallback((e: React.FocusEvent) => {
    // Focus landing on the pause/play control itself is not an engagement
    // signal — it is the mechanism for controlling rotation. Counting it
    // would trap rotation paused after the user clicks Play, because the
    // button keeps focus and would immediately re-assert the focus pause.
    isFocusedRef.current = e.target !== pauseButtonRef.current;
    syncPause();
  }, [syncPause]);
  const handleFocusOut = useCallback((e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      isFocusedRef.current = false;
      syncPause();
    }
  }, [syncPause]);

  const handleVideoPlay = useCallback(() => { isVideoOpenRef.current = true; syncPause(); }, [syncPause]);
  const handleVideoClose = useCallback(() => { isVideoOpenRef.current = false; syncPause(); }, [syncPause]);

  // Keep the pause ref in sync when the explicit control toggles, so the
  // running interval sees the change on its next tick without a restart.
  useEffect(() => { syncPause(); }, [syncPause]);

  // Focus handoff after manual navigation reaches an end. This must run
  // post-render (useLayoutEffect), because inside the click handler the
  // chevrons' disabled state has not been re-rendered yet — with only two
  // panels, the opposite chevron is still disabled at click time and
  // focus() would be a no-op, dropping focus to <body> and resuming
  // rotation. After the render the target chevron is enabled.
  useLayoutEffect(() => {
    if (!focusHandoffRef.current) return;
    focusHandoffRef.current = false;
    if (items.length < 2) return;
    const current = Math.min(activeIndex, items.length - 1);
    if (current === items.length - 1) {
      prevButtonRef.current?.focus();
    } else if (current === 0) {
      nextButtonRef.current?.focus();
    }
  }, [activeIndex, items.length]);

  if (items.length === 0) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- dev-only misconfig signal, mirrors Carousel/ContentGrid
      console.warn(
        '[TestimonialCarousel] No testimonials provided — populate at least two testimonial{N}Quote + testimonial{N}Name slot pairs.',
      );
    }
    return (
      <div
        data-poly-error="testimonial-carousel-empty"
        data-poly-error-detail="No testimonial{N}Quote + testimonial{N}Name slot pairs populated"
        aria-hidden="true"
        hidden
      />
    );
  }

  if (items.length < 2 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console -- dev-only signal; the 2-6 range is the editor contract
    console.warn(
      '[TestimonialCarousel] Only one testimonial provided — the block expects between 2 and 6.',
    );
  }

  const safeActiveIndex = Math.min(activeIndex, items.length - 1);
  const showNav = items.length > 1;

  // Manual navigation restarts the rotation interval so the next
  // auto-advance is a full AUTO_ROTATE_MS away from the user's action, and
  // flags the focus-handoff effect to run after the re-render.
  const selectIndex = (idx: number) => {
    setActiveIndex(idx);
    setRestartTrigger(t => t + 1);
    focusHandoffRef.current = true;
  };

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className="testimonial-carousel"
      aria-roledescription="carousel"
      aria-label={ariaLabel || heading || 'Testimonials'}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      <div className="testimonial-carousel__container">
        {(heading || showNav) && (
          <header className="testimonial-carousel__header">
            {heading && <h2 className="testimonial-carousel__heading">{heading}</h2>}
            {showNav && (
              <div className="testimonial-carousel__nav" role="group" aria-label="Carousel navigation">
                {!prefersReducedMotion && (
                  <button
                    type="button"
                    ref={pauseButtonRef}
                    className="testimonial-carousel__nav-button testimonial-carousel__nav-button--pause"
                    aria-label={userPaused ? 'Play testimonial rotation' : 'Pause testimonial rotation'}
                    aria-pressed={userPaused}
                    onClick={() => setUserPaused(prev => !prev)}
                  >
                    <CustomIcon name={userPaused ? 'play' : 'pause'} size={32} aria-hidden="true" />
                  </button>
                )}
                <button
                  type="button"
                  ref={prevButtonRef}
                  className="testimonial-carousel__nav-button testimonial-carousel__nav-button--prev"
                  aria-label="Previous testimonial"
                  onClick={() => selectIndex(safeActiveIndex - 1)}
                  disabled={safeActiveIndex === 0}
                >
                  <CustomIcon name="chevron-left-lg" size={32} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  ref={nextButtonRef}
                  className="testimonial-carousel__nav-button testimonial-carousel__nav-button--next"
                  aria-label="Next testimonial"
                  onClick={() => selectIndex(safeActiveIndex + 1)}
                  disabled={safeActiveIndex === items.length - 1}
                >
                  <CustomIcon name="chevron-right-lg" size={32} aria-hidden="true" />
                </button>
              </div>
            )}
          </header>
        )}

        <div className="testimonial-carousel__panels">
          {items.map((item, idx) => (
            <div
              key={`${item.name}-${idx}`}
              className={`testimonial-carousel__panel${
                idx === safeActiveIndex ? ' testimonial-carousel__panel--active' : ''
              }`}
              aria-hidden={idx !== safeActiveIndex}
            >
              <Testimonial
                quote={item.quote}
                name={item.name}
                title={item.title ?? ''}
                eyebrow={item.eyebrow}
                eyebrowLogoSrc={item.eyebrowLogoSrc}
                eyebrowLogoAlt={item.eyebrowLogoAlt}
                heading={item.heading}
                headshotSrc={item.headshotSrc}
                headshotAlt={item.headshotAlt}
                mediaSrc={item.mediaSrc}
                mediaAlt={item.mediaAlt}
                videoUrl={item.videoUrl}
                linkText={item.linkText}
                linkHref={item.linkHref}
                linkAction={item.linkAction}
                linkTargetPopupId={item.linkTargetPopupId}
                ctaText={item.ctaText}
                ctaHref={item.ctaHref}
                ctaAction={item.ctaAction}
                ctaTargetPopupId={item.ctaTargetPopupId}
                variant={variant}
                onVideoPlay={handleVideoPlay}
                onVideoClose={handleVideoClose}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
